import React, { useEffect, useState } from "react";
import { logger as SentryLogger } from "@sentry/react";
import { useNavigate, useParams } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import Button, { ButtonStyle } from "@components/buttons/Button/Button";
import ModalContainer from "@components/modal/Container";
import CircularSpinner from "@components/spinners/Circular/Circular";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import {
  IPricingRecord,
  PricingRecordStatus,
  QuoteBuilderService,
} from "@services/QuoteBuilder/QuoteBuilderService";
import { Country } from "@utils/constants/localisation";
import { STATUS_CONFIG } from "@utils/quoteStatusHelpers";
import { getZohoPotentialLink } from "@utils/misc/functions";
import { CustomerPotentialDetail } from "./customerPotentialDetailReducer";
import { PricingRecordAction } from "./pricingRecordReducer";
import PlanSummaryComponent from "./PlanSummaryComponent";
import PriceBreakdownComponent from "./PriceBreakdownComponent";
import QuoteStatusBanner from "./QuoteStatusBanner";
import { PlanType } from "@utils/quotePlanOptions";

type Props = {
  customerPotentialDetailState: CustomerPotentialDetail;
  pricingRecordState: IPricingRecord | null;
  pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
  navigateLandingPageTarget: string;
  navigateNewQuoteTarget: string;
};

export default function QuoteStatusView({
  customerPotentialDetailState,
  pricingRecordState,
  pricingRecordDispatch,
  navigateLandingPageTarget,
  navigateNewQuoteTarget
}: Props) {
  const navigate = useNavigate();
  const { potentialId, country } = useParams() as {
    potentialId: string;
    country: Country;
  };
  const msalContext = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (
      customerPotentialDetailState?.error == null &&
      pricingRecordState === null
    ) {
      navigate(navigateLandingPageTarget);
    }
  }, [
    pricingRecordState,
    navigateLandingPageTarget,
    navigate,
    customerPotentialDetailState,
  ]);

  /*
   * Button click handler for quote actions
   */
  const handleButtonClick = async (action: string) => {
    // Some buttons appear only on certain quote statuses. So this switch case
    // is for handling different button actions.
    switch (action) {
      case "recall-quote-action": {
        console.log("Recalling quote for pricing record:", pricingRecordState);
        setLoading(true);
        const response = await QuoteBuilderService.postPricingRecord(
          country,
          potentialId,
          {
            action: "recall_quote",
          },
          { msalContext, isAuthenticated }
        );
        setLoading(false);
        console.log("Successfully recalled pricing record: ", response);
        // Update the pricing record in the state
        pricingRecordDispatch({
          type: "SET_PRICING_RECORD",
          payload: response.pricing_record,
        });
        // FIXME: Display error if the response contains an error

        // Log the pricing record
        SentryLogger.info(
          "Recalled quote",
          {
            customerPotentialDetailState,
            pricingRecord: response.pricing_record,
          }
        );
        break;
      }
      case "resend-quote-action": {
        console.log("Resending quote for pricing record:", pricingRecordState);
        setLoading(true);
        const response = await QuoteBuilderService.postPricingRecord(
          country,
          potentialId,
          {
            action: "resend_quote",
          },
          { msalContext, isAuthenticated }
        );
        setLoading(false);
        console.log("Successfully resent pricing record: ", response);
        // Update the pricing record in the state
        pricingRecordDispatch({
          type: "SET_PRICING_RECORD",
          payload: response.pricing_record,
        });
        // FIXME: Display error if the response contains an error

        // Log the pricing record
        SentryLogger.info("Resent quote", {
          customerPotentialDetailState,
          pricingRecord: response.pricing_record,
        });
        break;
      }
      case "create-new-quote-action": {
        // When creating a new quote, Sales may update Potential details in Zoho,
        // (e.g. Plan Type), which affects the filtering of plans after redirecting
        // to the Plan Details page. To cover this use case, the system must fetch
        // the latest Potential details by navigating to the Quote Landing page 
        // with `{ state: { forceNewQuote: true } }` to leverage existing error
        // checking while forcing the new quote flow. This helps avoid duplication
        // of fetch logic and validation.

        console.log("Creating new quote for pricing record:", pricingRecordState);
        navigate(navigateNewQuoteTarget, { state: { forceNewQuote: true } });
        break;
      }
      case "create-flexi-temp-potential": {
        setLoading(true);
        const response = await QuoteBuilderService.postPricingRecord(
          country,
          potentialId,
          {
            action: "create_flexi_temp_potential",
          },
          { msalContext, isAuthenticated }
        );

        window.open(getZohoPotentialLink(country, response.pricing_record.zoho_potential.Linked_Potential.id), '_blank')

        pricingRecordDispatch({
          type: "SET_PRICING_RECORD",
          payload: response.pricing_record,
        });
        // FIXME: Display error if the response contains an error

        // Log the pricing record
        SentryLogger.info(
          "Create Flexi Temp Potential",
          {
            customerPotentialDetailState,
            pricingRecord: response.pricing_record,
          }
        );

        setLoading(false);
        break;
      }
      case "view-flexi-temp-potential": {
        window.open(getZohoPotentialLink(country, pricingRecordState?.zoho_potential?.Linked_Potential?.id), '_blank')
        break;
      }
      // TODO: Add other button actions here such as:
      // - View Potential
      default:
        console.warn(`Unknown action: ${action}`);
    }
  };

  return (
    <>
      <NavBar pageTitle="Sales Quote" />
      {loading === true && <ForegroundLoadingSpinner />}
      <form style={{ margin: "2.5vh 2.5vw" }}>
        {/* The following section is shown when there is an existing pricing record */}
        {pricingRecordState && (
          <QuoteStatusBanner
            status={pricingRecordState.status}
            zohoSignQuoteDetail={pricingRecordState.zoho_sign_quote_detail}
            customerName={customerPotentialDetailState.customerName || ""}
          />
        )}

        {pricingRecordState && (
          <>
            <PlanSummaryComponent
              customerPotentialDetailState={customerPotentialDetailState}
              pricingRecordState={pricingRecordState}
            />
            <Divider />
            <PriceBreakdownComponent
              pricingRecord={pricingRecordState}
              showTotalWeeklySubscriptionPrice={true}
              showSetupFee={true}
              defaultExpanded={true}
            />
          </>
        )}
        {/* Show buttons based on the quote status */}
        {pricingRecordState && (
          <CustomButtons
            pricingRecord={pricingRecordState}
            onButtonClick={handleButtonClick}
            loading={loading}
          />
        )}
      </form>
    </>
  );
}

/**
 * Helper components
 */
const Divider = () => (
  <hr
    style={{ margin: "2vh 0", border: "none", borderTop: "1px solid #e0e0e0" }}
  />
);

const ForegroundLoadingSpinner = () => (
  <ModalContainer stylesCfg={{ windowTransparency: true }}>
    <LoadingSpinner />
  </ModalContainer>
);

const LoadingSpinner = () => (
  <div
    data-testid="loading-spinner"
    style={{ display: "flex", justifyContent: "center", margin: "15vh" }}
  >
    {<CircularSpinner />}
  </div>
);

const CustomButtons = ({
  pricingRecord,
  onButtonClick,
  loading
}: {
  pricingRecord: IPricingRecord;
  onButtonClick: (action: string) => void;
  loading: boolean;
}) => {
  const config = STATUS_CONFIG[pricingRecord.status];

  // Conditionally adding buttons for QUOTE_SIGNED
  if (pricingRecord.status === PricingRecordStatus.QUOTE_SIGNED && pricingRecord.plan === PlanType.FLEXI_OWN) {
    config.buttons = []
    if (pricingRecord.zoho_potential?.Linked_Potential?.id) {
      config.buttons.push(
        {
          label: "View Linked Flexi Temp Potential",
          action: "view-flexi-temp-potential",
        }
      )
    }
    else {
      config.buttons.push(
        {
          label: "Create Flexi Temp Potential",
          action: "create-flexi-temp-potential",
        }
      )
    }
  }

  if (!config?.buttons) return null;

  return (
    <div
      style={{
        marginTop: "2vh",
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      {config.buttons.map((button, index) => (
        <Button
          key={`${button.action}-${index}`}
          label={button.label}
          onClickFunc={() => onButtonClick(button.action)}
          disabled={loading}
          testId={`button-${button.action}`}
          btnType={ButtonStyle.primary}
        />
      ))}
    </div>
  );
};
